import { useState } from 'react';
import { Typography } from '@mui/material';
import type { CreatePlanWizardValues } from '../../types';
import { ContentBox } from '@/components/shared/content-box';
import { useNotification } from '@/context/notification';
import { IdpEvent } from '@/constants/analytics';
import { useWizard } from '@/context/wizard';
import { useUpdateProfileBasedRecommendation } from '@/data/idp/profile-based-recommendations';
import { RecommendationPlanIds } from '@/data/idp/profile-based-recommendations/interfaces';
import { StepActions } from '@/packages/idp/pages/create-plan/components/step-actions';
import { track } from '@/utils/analytics';
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleContent,
} from '@/components/shared/toggle-button-group';
import { IconName } from '@/components/shared/icon/icon-map';

type DurationValue = number | null;
type DurationOption = {
  value: DurationValue;
  icon: IconName;
};

const presetDurations: DurationOption[] = [
  { value: 3, icon: 'calendar-date-3' },
  { value: 6, icon: 'calendar-date-6' },
  { value: 12, icon: 'calendar-date-12' },
];

export const Duration = () => {
  const { actions, values } = useWizard<CreatePlanWizardValues>();
  const [selectedDuration, setSelectedDuration] = useState<DurationValue>(
    values?.duration ?? null
  );
  const { mutate, isPending } = useUpdateProfileBasedRecommendation(
    values?.id || ''
  );
  const { toast } = useNotification();

  const handleChange = (newValue: DurationValue) => {
    if (newValue !== null) {
      setSelectedDuration(newValue);
    }
  };

  const handleNext = () => {
    if (selectedDuration === null) return;

    const hasFeedback =
      !!values?.[RecommendationPlanIds.Feedback]?.currentFeedback;
    const nextStep = hasFeedback
      ? RecommendationPlanIds.Feedback
      : RecommendationPlanIds.NewFeedback;

    mutate(
      {
        duration_in_months: selectedDuration,
        current_step: nextStep,
        plan: { duration_in_months: selectedDuration },
      },
      {
        onSuccess: () => {
          track(IdpEvent.WizardStepCompleted, {
            plan_id: values?.planId,
            step: RecommendationPlanIds.Duration,
            duration_months: selectedDuration,
          });
          actions.updateValues({ duration: selectedDuration });
          actions.navigate(nextStep);
        },
        onError: () => {
          toast({
            content:
              'Erro ao atualizar a duração do plano. Tente novamente mais tarde.',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <ContentBox spacing={3}>
      <Typography variant="h2">
        Você gostaria de realizar o plano em quanto tempo?
      </Typography>

      <ToggleButtonGroup
        value={selectedDuration}
        onChange={handleChange}
        aria-label="Selecione a duração do plano"
        direction={{ md: 'row' }}
      >
        {presetDurations.map((duration) => (
          <ToggleButton key={duration.value} value={duration.value}>
            <ToggleContent
              icon={duration.icon}
              title={`${duration.value} meses`}
            />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <StepActions
        onNext={handleNext}
        nextLabel="Próximo"
        nextDisabled={selectedDuration === null}
        nextLoading={isPending}
      />
    </ContentBox>
  );
};
